import { gql, useMutation, useQuery } from "@apollo/client";
import {
	Button,
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	useToast,
	Select,
	InputGroup,
	NumberInput,
	NumberInputField,
	FormHelperText,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Loading } from "src/components/Loading";
import { RequestFailed } from "src/components/RequestFailed";
import { useProfile } from "src/hooks/useProfile";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { Form, FormField, FormItem, FormMessage } from "src/components/ui/form";
import { Button as PopoverTriggerButton } from "src/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "src/components/ui/popover";
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
	CommandEmpty,
} from "src/components/ui/command";
import { Badge } from "src/components/ui/badge";
import { PlusIcon, XIcon } from "lucide-react";
import { Fragment } from "react";
import { JOB_TAGS } from "src/common/graphql/typed-queries";
import { logEvent } from "src/utlis/analytics";
import { Select as CRSelect } from "chakra-react-select";
import countries from "src/assets/countries.json";
import currencies from "src/assets/currencies.json";
import { AddressSelector } from "src/components/AddressSelector";
import { Gender, ProLeadSource, ReasonForJoiningLaborHack } from "src/types";
import { EmploymentStatus, PhoneType } from "src/__generated__/graphql";
import { useToggles, TOGGLE_KEYS } from "src/hooks/useToggles";
import {
	leadSourceOptions,
	phoneTypeOptions,
	reasonsForJoiningLaborHackOptions,
} from "src/pages/trades/complete-profile/constants";

const UPDATE_PRO_APPLICATION = gql(`
  mutation UpdateMyProfile(
    $firstName: String
    $lastName: String
    $dateOfBirth: DateTimeISO
    $gender: Gender
    $jobTitle: String
    $tagIds: [String!]
    $customTagIds: [String!]
    $yearsOfExperience: Float
    $country: String
    $detailedAddress: AddressInput
    $employmentStatus: EmploymentStatus
    $incomeCurrency: String
    $averageMonthlyIncome: Float
    $numberOfDaysWorkedPerWeek: Float
    $phoneType: PhoneType
    $leadSource: String
    $leadSourceText: String
    $reasonForJoiningLaborHack: String
    $reasonsForJoiningLaborHack: [String!]
    $reasonForJoiningLaborHackOtherText: String
    $phoneNumber: String
  ) {
    updateMyProApplication(
      firstName: $firstName
      lastName: $lastName
      dateOfBirth: $dateOfBirth
      gender: $gender
      jobTitle: $jobTitle
      tagIds: $tagIds
      customTagIds: $customTagIds
      yearsOfExperience: $yearsOfExperience
      country: $country
      detailedAddress: $detailedAddress
      employmentStatus: $employmentStatus
      incomeCurrency: $incomeCurrency
      averageMonthlyIncome: $averageMonthlyIncome
      numberOfDaysWorkedPerWeek: $numberOfDaysWorkedPerWeek
      phoneType: $phoneType
      leadSource: $leadSource
      leadSourceText: $leadSourceText
      reasonForJoiningLaborHack: $reasonForJoiningLaborHack
      reasonsForJoiningLaborHack: $reasonsForJoiningLaborHack
      reasonForJoiningLaborHackOtherText: $reasonForJoiningLaborHackOtherText
      phoneNumber: $phoneNumber
    ) {
      id
      firstName
      lastName
      dateOfBirth
      gender
      jobTitle
      tags { id name group }
      customTags { id name }
      yearsOfExperience
      phoneNumber
      country
      employmentStatus
      incomeCurrency
      averageMonthlyIncome
      numberOfDaysWorkedPerWeek
      phoneType
      leadSource
      leadSourceText
      reasonForJoiningLaborHack
      reasonsForJoiningLaborHack
      reasonForJoiningLaborHackOtherText
      detailedAddress { address formattedAddress state city placeId }
    }
  }
`);

const formSchema = z
	.object({
		// Required fields from ApplicationFormV2
		firstName: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.string()
		),
		lastName: z.preprocess((val) => (val === "" ? undefined : val), z.string()),
		dateOfBirth: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.string()
		),
		gender: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.nativeEnum(Gender)
		),
		jobTitle: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.string().nonempty("Profession is required")
		),
		country: z.object({
			label: z.string(),
			value: z.string(),
		}),
		phoneNumber: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.string()
		),
		detailedAddress: z.object({
			placeId: z.string(),
			address: z.string(),
			formattedAddress: z.string(),
			state: z.string().optional(),
			city: z.string().optional(),
		}),
		employmentStatus: z.nativeEnum(EmploymentStatus, {
			required_error: "Employment status is required",
		}),
		incomeCurrency: z.object(
			{
				label: z.string(),
				value: z.string(),
			},
			{
				required_error: "Income currency is required",
			}
		),
		averageMonthlyIncome: z.preprocess(
			(val) => (val === 0 ? undefined : val),
			z.number().optional()
		),
		noExperience: z.boolean(),
		numberOfDaysWorkedPerWeek: z.preprocess(
			(val) => (val === "" ? undefined : Number(val)),
			z.number({
				required_error: "Number of days worked per week is required",
			})
		),
		phoneType: z.nativeEnum(PhoneType, {
			required_error: "Phone type is required",
			invalid_type_error: "Please select a phone type",
		}),
		leadSource: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.nativeEnum(ProLeadSource, {
				required_error: "Lead source is required",
			})
		),
		reasonForJoiningLaborHack: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.nativeEnum(ReasonForJoiningLaborHack).optional()
		),
		reasonsForJoiningLaborHack: z
			.array(z.nativeEnum(ReasonForJoiningLaborHack))
			.optional(),

		// Optional fields from ApplicationFormV2
		tags: z.array(z.string()).optional(),
		customTags: z.array(z.string()).optional(),
		yearsOfExperience: z
			.preprocess((val) => Number(val), z.number())
			.optional(),
		leadSourceText: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.string().optional()
		),
		reasonForJoiningLaborHackOtherText: z.preprocess(
			(val) => (val === "" ? undefined : val),
			z.string().optional()
		),
	})
	.refine(
		(data) => {
			if (data.firstName && data.firstName.includes(" ")) {
				return false;
			}
			return true;
		},
		{ message: "First name cannot contain spaces", path: ["firstName"] }
	)
	.refine(
		(data) => {
			if (data.lastName && data.lastName.includes(" ")) {
				return false;
			}
			return true;
		},
		{ message: "Last name cannot contain spaces", path: ["lastName"] }
	)
	.refine(
		(data) => {
			if (data.tags && data.tags.length > 0) {
				return true;
			}
			if (data.customTags && data.customTags.length > 0) {
				return true;
			}
			return false;
		},
		{
			message: "At least one tag is required",
			path: ["tags"],
		}
	)
	.refine(
		(data) => {
			if (
				!data.noExperience &&
				data.yearsOfExperience !== undefined &&
				data.yearsOfExperience < 1
			) {
				return false;
			}
			return true;
		},
		{
			message: "Years of experience is required",
			path: ["yearsOfExperience"],
		}
	)
	.refine((data) => {
		if (data.reasonForJoiningLaborHack === ReasonForJoiningLaborHack.OTHER) {
			return !!data.reasonForJoiningLaborHackOtherText;
		}
		if (data.reasonsForJoiningLaborHack?.includes(ReasonForJoiningLaborHack.OTHER)) {
			return !!data.reasonForJoiningLaborHackOtherText;
		}
		return true;
	})
	.refine((data) => {
		if (data.leadSource === ProLeadSource.OTHER) {
			return !!data.leadSourceText;
		}
		return true;
	});

type FormValues = z.infer<typeof formSchema>;

export default function SettingsPage() {
	const navigate = useNavigate();
	const toast = useToast();
	const { toggles, isLoading: isLoadingToggles } = useToggles();
	const multiSelectReasonsEnabled = !!toggles[TOGGLE_KEYS.MULTI_SELECT_REASONS_FOR_JOINING];

	const {
		profile,
		loading: loadingProfile,
		error: profileError,
	} = useProfile();

	const {
		data: tagsData,
		loading: loadingTags,
		error: tagsError,
	} = useQuery(JOB_TAGS);

	const [updateProApplication, { loading: updating, error: updateError }] =
		useMutation(UPDATE_PRO_APPLICATION, {
			onCompleted: () => {
				logEvent("pro_profile_updated_in_settings", {
					jobTitle: form.getValues("jobTitle"),
					tags: form.getValues("tags"),
					customTags: form.getValues("customTags"),
				});

				toast({
					title: "Profile updated",
					status: "success",
					duration: 3000,
					isClosable: true,
				});

				setTimeout(() => {
					const redirectAfterCompletion = localStorage.getItem(
						"redirectAfterCompletion"
					);

					if (redirectAfterCompletion) {
						localStorage.removeItem("redirectAfterCompletion");
						window.location.href = redirectAfterCompletion;
					}
				}, 2000);
			},
			refetchQueries: ["GetMyProfile"],
		});

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			firstName: undefined,
			lastName: undefined,
			dateOfBirth: undefined,
			gender: undefined,
			jobTitle: undefined,
			tags: [],
			customTags: [],
			country: undefined,
			phoneNumber: undefined,
			detailedAddress: undefined,
			employmentStatus: undefined,
			incomeCurrency: undefined,
			averageMonthlyIncome: undefined,
			yearsOfExperience: undefined,
			noExperience: false,
			numberOfDaysWorkedPerWeek: undefined,
			phoneType: undefined,
			leadSource: undefined,
			leadSourceText: undefined,
			reasonForJoiningLaborHack: undefined,
			reasonsForJoiningLaborHack: [],
			reasonForJoiningLaborHackOtherText: undefined,
		},
	});

	// Refs for form fields to enable scrolling to errors
	const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

	// Function to scroll to the first error field
	const scrollToFirstError = () => {
		const errors = form.formState.errors;
		const errorFields = Object.keys(errors);

		if (errorFields.length === 0) return;

		// Define the order of fields as they appear in the form
		const fieldOrder = [
			"firstName",
			"lastName",
			"phoneNumber",
			"dateOfBirth",
			"gender",
			"jobTitle",
			"yearsOfExperience",
			"country",
			"detailedAddress",
			"employmentStatus",
			"averageMonthlyIncome",
			"incomeCurrency",
			"numberOfDaysWorkedPerWeek",
			"phoneType",
			"leadSource",
			"leadSourceText",
			"reasonForJoiningLaborHack",
			"reasonForJoiningLaborHackOtherText",
			"tags",
		];

		// Find the first error field in the order they appear
		const firstErrorField = fieldOrder.find((field) =>
			errorFields.includes(field)
		);

		if (firstErrorField && fieldRefs.current[firstErrorField]) {
			const element = fieldRefs.current[firstErrorField];
			if (element) {
				element.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "nearest",
				});

				// Focus the field if it's focusable
				const focusableElement = element.querySelector(
					"input, select, textarea"
				) as HTMLElement;
				if (focusableElement && typeof focusableElement.focus === "function") {
					setTimeout(() => focusableElement.focus(), 100);
				}
			}
		}
	};

	useEffect(() => {
		if (profile) {
			// Basic profile fields
			form.setValue("firstName", profile.firstName);
			form.setValue("lastName", profile.lastName);
			form.setValue(
				"jobTitle",
				profile.jobTitle || profile.proApplication?.category?.proTitle || ""
			);
			form.setValue("phoneNumber", profile.phoneNumber);
			form.setValue("tags", profile.tags?.map((tag) => tag.id) || []);
			form.setValue(
				"customTags",
				profile.customTags?.map((tag) => tag.id) || []
			);
			if (
				profile.yearsOfExperience !== undefined &&
				profile.yearsOfExperience !== null
			) {
				form.setValue("yearsOfExperience", profile.yearsOfExperience);
			}
			if (profile.yearsOfExperience === 0) {
				form.setValue("noExperience", true);
			}

			// ProApplication fields
			if (profile.proApplication) {
				const proApp = profile.proApplication;

				if (proApp.dateOfBirth) {
					const iso = new Date(proApp.dateOfBirth).toISOString().split("T")[0];
					form.setValue("dateOfBirth", iso);
				}
				if (proApp.gender) {
					form.setValue("gender", proApp.gender as unknown as Gender);
				}
				if (proApp.country) {
					const entry = Object.entries(countries).find(
						([, c]) => c.name === proApp.country
					);
					if (entry) {
						const [code, c] = entry as [
							string,
							(typeof countries)[keyof typeof countries]
						];
						form.setValue("country", { value: code, label: c.name });
					}
				}
				if (proApp.detailedAddress && proApp.detailedAddress.placeId) {
					form.setValue("detailedAddress", {
						placeId: proApp.detailedAddress.placeId,
						address: proApp.detailedAddress.address,
						formattedAddress: proApp.detailedAddress.formattedAddress,
						state: proApp.detailedAddress.state || undefined,
						city: proApp.detailedAddress.city || undefined,
					});
				}
				if (proApp.incomeCurrency) {
					const currencyEntry = Object.entries(currencies).find(
						([, cur]) => cur.name === proApp.incomeCurrency
					);
					if (currencyEntry) {
						const [code, cur] = currencyEntry as [
							string,
							(typeof currencies)[keyof typeof currencies]
						];
						form.setValue("incomeCurrency", { value: code, label: cur.name });
					}
				}
				if (proApp.employmentStatus) {
					form.setValue("employmentStatus", proApp.employmentStatus);
				}
				if (
					proApp.averageMonthlyIncome !== null &&
					proApp.averageMonthlyIncome !== undefined
				) {
					form.setValue("averageMonthlyIncome", proApp.averageMonthlyIncome);
				}
				if (
					proApp.numberOfDaysWorkedPerWeek !== null &&
					proApp.numberOfDaysWorkedPerWeek !== undefined
				) {
					form.setValue(
						"numberOfDaysWorkedPerWeek",
						proApp.numberOfDaysWorkedPerWeek
					);
				}
				if (proApp.phoneType) {
					form.setValue("phoneType", proApp.phoneType);
				}
				if (proApp.leadSource) {
					form.setValue("leadSource", proApp.leadSource as ProLeadSource);
				}
				if (proApp.leadSourceText) {
					form.setValue("leadSourceText", proApp.leadSourceText);
				}
				if (proApp.reasonForJoiningLaborHack) {
					form.setValue(
						"reasonForJoiningLaborHack",
						proApp.reasonForJoiningLaborHack as ReasonForJoiningLaborHack
					);
				}
				if (proApp.reasonsForJoiningLaborHack?.length) {
					form.setValue(
						"reasonsForJoiningLaborHack",
						proApp.reasonsForJoiningLaborHack as ReasonForJoiningLaborHack[]
					);
				}
				if (proApp.reasonForJoiningLaborHackOtherText) {
					form.setValue(
						"reasonForJoiningLaborHackOtherText",
						proApp.reasonForJoiningLaborHackOtherText
					);
				}
			}
		}
	}, [profile, form]);

	const onSubmit = (data: FormValues) => {
		const yearsOfExperience = data.noExperience
			? 0
			: Number(data.yearsOfExperience);
		updateProApplication({
			variables: {
				firstName: data.firstName,
				lastName: data.lastName,
				dateOfBirth: data.dateOfBirth
					? new Date(data.dateOfBirth).toISOString()
					: undefined,
				gender: data.gender,
				phoneNumber: data.phoneNumber,
				jobTitle: data.jobTitle,
				tagIds: data.tags,
				customTagIds: data.customTags,
				...(yearsOfExperience !== profile?.yearsOfExperience && {
					yearsOfExperience,
				}),
				country: data.country?.label,
				detailedAddress: data.detailedAddress,
				employmentStatus: data.employmentStatus,
				incomeCurrency: data.incomeCurrency?.label,
				averageMonthlyIncome: data.averageMonthlyIncome,
				numberOfDaysWorkedPerWeek: data.numberOfDaysWorkedPerWeek,
				phoneType: data.phoneType,
				leadSource: data.leadSource,
				leadSourceText: data.leadSourceText,
				...(multiSelectReasonsEnabled
					? {
							reasonsForJoiningLaborHack:
								data.reasonsForJoiningLaborHack,
					  }
					: {
							reasonForJoiningLaborHack:
								data.reasonForJoiningLaborHack,
					  }),
				reasonForJoiningLaborHackOtherText:
					data.reasonForJoiningLaborHackOtherText,
			},
		});
	};

	const onInvalidSubmit = () => {
		// Scroll to the first error when form submission fails validation
		setTimeout(() => scrollToFirstError(), 100);
	};

	if (loadingProfile || loadingTags || isLoadingToggles) {
		return <Loading />;
	}

	if (profileError || tagsError) {
		return <RequestFailed text={profileError?.message || tagsError?.message} />;
	}

	if (updateError) {
		return <RequestFailed text={updateError.message} />;
	}

	const tagGroups = tagsData?.jobAdTags.reduce((acc, tag) => {
		if (!acc[tag.group]) {
			acc[tag.group] = [];
		}
		acc[tag.group].push(tag);
		return acc;
	}, {} as Record<string, typeof tagsData.jobAdTags>);

	const customTags = form.watch("customTags") || [];
	const leadSource = form.watch("leadSource");
	const reasonForJoiningLaborHack = form.watch("reasonForJoiningLaborHack");
	const reasonsForJoiningLaborHack = form.watch("reasonsForJoiningLaborHack");
	const hasTags = Boolean(
		(form.watch("tags")?.length || 0) > 0 || customTags.length > 0
	);

	return (
		<>
			<Helmet>
				<title>Settings | LaborHack Pro</title>
			</Helmet>
			<div>
				<Button
					mb={{
						base: 4,
						lg: 6,
					}}
					colorScheme="prussianBlue"
					variant={"outline"}
					border={"1px solid #D7D7D7"}
					rounded={0}
					p={2}
					fontWeight={500}
					onClick={() => {
						navigate(-1);
					}}
					leftIcon={<ChevronLeftIcon w={5} h={5} />}
				>
					Back
				</Button>
				<div className="grid grid-cols-1 gap-2 lg:gap-4 border-y py-6 lg:py-10 max-w-screen-md">
					<h1 className="text-[#0E212D] text-[18px] lg:text-[23px] font-semibold">
						Profile Settings
					</h1>
					<div className="space-y-8 lg:space-y-12 w-full">
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)}
								className="space-y-8"
							>
								{/* Personal Information */}
								<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
									<FormControl
										isInvalid={!!form.formState.errors.firstName}
										ref={(el) => (fieldRefs.current["firstName"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												First Name
											</FormLabel>
											<Input
												disabled={true}
												{...form.register("firstName")}
												type="text"
												placeholder="Enter your first name"
												rounded={0}
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												border={"1px solid #D7D7D7"}
											/>
											<FormErrorMessage>
												{form.formState.errors.firstName?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
									<FormControl
										isInvalid={!!form.formState.errors.lastName}
										ref={(el) => (fieldRefs.current["lastName"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Last Name
											</FormLabel>
											<Input
												disabled={true}
												{...form.register("lastName")}
												type="text"
												placeholder="Enter your last name"
												rounded={0}
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												border={"1px solid #D7D7D7"}
											/>
											<FormErrorMessage>
												{form.formState.errors.lastName?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
									<FormControl isInvalid={false}>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Email
											</FormLabel>
											<Input
												disabled={true}
												value={profile?.email}
												type="email"
												placeholder="Enter your email"
												rounded={0}
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												border={"1px solid #D7D7D7"}
											/>
											<FormErrorMessage>
												{form.formState.errors.phoneNumber?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
									<FormControl
										isInvalid={!!form.formState.errors.phoneNumber}
										ref={(el) => (fieldRefs.current["phoneNumber"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Phone Number
											</FormLabel>
											<Input
												disabled={true}
												{...form.register("phoneNumber")}
												type="tel"
												placeholder="Enter your phone number"
												rounded={0}
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												border={"1px solid #D7D7D7"}
											/>
											<FormErrorMessage>
												{form.formState.errors.phoneNumber?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
									<FormControl
										isInvalid={!!form.formState.errors.dateOfBirth}
										ref={(el) => (fieldRefs.current["dateOfBirth"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Date of Birth
											</FormLabel>
											<Input
												disabled={true}
												{...form.register("dateOfBirth")}
												type="date"
												max={new Date().toISOString().split("T")[0]}
												placeholder="Enter your date of birth"
												rounded={0}
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												border={"1px solid #D7D7D7"}
											/>
											<FormErrorMessage>
												{form.formState.errors.dateOfBirth?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
									<FormControl
										isInvalid={!!form.formState.errors.gender}
										ref={(el) => (fieldRefs.current["gender"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Gender
											</FormLabel>
											<Select
												disabled={true}
												{...form.register("gender")}
												rounded={0}
												h={{ base: 10, lg: 16 }}
											>
												<option value={""}>Select Option</option>
												<option value={Gender.MALE}>Male</option>
												<option value={Gender.FEMALE}>Female</option>
											</Select>
											<FormErrorMessage>
												{form.formState.errors.gender?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
								</div>

								<FormControl
									isInvalid={!!form.formState.errors.jobTitle}
									ref={(el) => (fieldRefs.current["jobTitle"] = el)}
								>
									<div className="flex flex-col gap-3">
										<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
											Profession
										</FormLabel>
										<Input
											{...form.register("jobTitle")}
											type="text"
											placeholder="Enter your profession"
											rounded={0}
											py={{
												base: 5,
												lg: 8,
											}}
											px={{
												base: 3,
												lg: 5,
											}}
											border={"1px solid #D7D7D7"}
										/>
										<FormErrorMessage>
											{form.formState.errors.jobTitle?.message}
										</FormErrorMessage>
									</div>
								</FormControl>

								<FormControl
									isInvalid={!!form.formState.errors.yearsOfExperience}
									ref={(el) => (fieldRefs.current["yearsOfExperience"] = el)}
								>
									<div className="flex flex-col gap-3">
										<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
											Years of experience
										</FormLabel>
										<div className="flex flex-col gap-1">
											<FormControl
												isInvalid={
													!form.getValues("noExperience") &&
													!!form.formState.touchedFields.noExperience &&
													!form.getValues("yearsOfExperience")
												}
											>
												<div className="flex items-center gap-2">
													<Checkbox
														id="noExperience"
														colorScheme="brand"
														{...form.register("noExperience")}
														isDisabled={!!form.watch("yearsOfExperience")}
													/>
													<FormLabel
														fontSize={"14px"}
														color={"gray.500"}
														margin={0}
														fontWeight={"normal"}
														htmlFor="proDoesNotWishToDiscloseBudget"
													>
														No experience
													</FormLabel>
												</div>
											</FormControl>
											<Input
												{...form.register("yearsOfExperience")}
												type="number"
												placeholder="Enter your years of experience"
												isDisabled={!!form.watch("noExperience")}
												rounded={0}
												py={{
													base: 5,
													lg: 8,
												}}
												px={{
													base: 3,
													lg: 5,
												}}
												border={"1px solid #D7D7D7"}
											/>
											<FormErrorMessage>
												{form.formState.errors.yearsOfExperience?.message}
											</FormErrorMessage>
										</div>
									</div>
								</FormControl>
								{/* Location & Professional Information */}
								<div className="grid grid-cols-1 gap-6">
									<FormControl
										isInvalid={!!form.formState.errors.country}
										ref={(el) => (fieldRefs.current["country"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Country
											</FormLabel>
											<Controller
												name="country"
												control={form.control}
												render={({ field }) => (
													<CRSelect
														placeholder="Select option"
														options={Object.values(countries).map(
															(country) => ({
																label: country.name,
																value: country.alpha2,
															})
														)}
														chakraStyles={{
															control: (baseStyles: any) => ({
																...baseStyles,
																height: { base: "40px", lg: "70px" },
																borderRadius: 0,
															}),
														}}
														{...field}
													/>
												)}
											/>
											<FormErrorMessage>
												{form.formState.errors.country?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
									<div
										ref={(el) => (fieldRefs.current["detailedAddress"] = el)}
									>
										<Controller
											name="detailedAddress"
											control={form.control}
											render={({ field }) => (
												<AddressSelector
													value={field.value as any}
													onChange={field.onChange}
													isInvalid={!!form.formState.errors.detailedAddress}
												/>
											)}
										/>
									</div>

									<FormControl
										isInvalid={!!form.formState.errors.employmentStatus}
										ref={(el) => (fieldRefs.current["employmentStatus"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Employment Status
											</FormLabel>
											<Controller
												name="employmentStatus"
												control={form.control}
												render={({ field }) => (
													<CRSelect
														placeholder="Select employment status"
														options={[
															{
																value: EmploymentStatus.Employed,
																label: "Employed",
															},
															{
																value: EmploymentStatus.SelfEmployed,
																label: "Self Employed",
															},
															{
																value: EmploymentStatus.Unemployed,
																label: "Unemployed",
															},
														]}
														chakraStyles={{
															control: (baseStyles: any) => ({
																...baseStyles,
																height: { base: "40px", lg: "70px" },
																borderRadius: 0,
															}),
														}}
														value={
															field.value
																? [
																		{
																			value: EmploymentStatus.Employed,
																			label: "Employed",
																		},
																		{
																			value: EmploymentStatus.SelfEmployed,
																			label: "Self Employed",
																		},
																		{
																			value: EmploymentStatus.Unemployed,
																			label: "Unemployed",
																		},
																	].find((o) => o.value === field.value)
																: null
														}
														onChange={(selected) =>
															field.onChange(selected?.value)
														}
													/>
												)}
											/>
											<FormErrorMessage>
												{form.formState.errors.employmentStatus?.message}
											</FormErrorMessage>
										</div>
									</FormControl>

									{form.watch("employmentStatus") &&
										form.watch("employmentStatus") !==
											EmploymentStatus.Unemployed && (
											<FormControl
												isInvalid={!!form.formState.errors.averageMonthlyIncome}
												ref={(el) =>
													(fieldRefs.current["averageMonthlyIncome"] = el)
												}
											>
												<div className="flex flex-col gap-3">
													<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
														Average Monthly Income
													</FormLabel>
													<InputGroup>
														<NumberInput
															className="w-full"
															onChange={(_, value) => {
																form.setValue(
																	"averageMonthlyIncome",
																	value as any
																);
															}}
															value={form.watch("averageMonthlyIncome") as any}
															rounded={0}
															step={5000}
														>
															<NumberInputField
																disabled={
																	profile?.proApplication
																		?.averageMonthlyIncome !== undefined
																}
																rounded={0}
																border={"1px solid #D7D7D7"}
																py={{ base: 5, lg: 8 }}
																placeholder="e.g. 100,000"
																px={{ base: 3, lg: 5 }}
															/>
														</NumberInput>
													</InputGroup>
													<FormHelperText>
														I earn this monthly on average
													</FormHelperText>
													<FormErrorMessage>
														{
															form.formState.errors.averageMonthlyIncome
																?.message
														}
													</FormErrorMessage>
												</div>
											</FormControl>
										)}

									<FormControl
										isInvalid={!!form.formState.errors.incomeCurrency}
										ref={(el) => (fieldRefs.current["incomeCurrency"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Income Currency
											</FormLabel>
											<Controller
												name="incomeCurrency"
												control={form.control}
												render={({ field }) => (
													<CRSelect
														isDisabled={
															!!profile?.proApplication?.incomeCurrency
														}
														placeholder="Select option"
														options={Object.values(currencies).map(
															(currency) => ({
																label: currency.name,
																value: currency.code,
															})
														)}
														chakraStyles={{
															control: (baseStyles: any) => ({
																...baseStyles,
																height: { base: "40px", lg: "70px" },
																borderRadius: 0,
															}),
														}}
														{...field}
													/>
												)}
											/>
											<FormErrorMessage>
												{form.formState.errors.incomeCurrency?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
								</div>

								{/* Other Information */}
								<div className="grid grid-cols-1 gap-6">
									<FormControl
										isInvalid={
											!!form.formState.errors.numberOfDaysWorkedPerWeek
										}
										ref={(el) =>
											(fieldRefs.current["numberOfDaysWorkedPerWeek"] = el)
										}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Days worked per week
											</FormLabel>
											<Select
												{...form.register("numberOfDaysWorkedPerWeek")}
												rounded={0}
												h={{ base: 10, lg: 16 }}
											>
												<option value={""}>Select Option</option>
												{[0, 1, 2, 3, 4, 5, 6, 7].map((value) => (
													<option key={value} value={value}>
														{value}
													</option>
												))}
											</Select>
											<FormErrorMessage>
												{
													form.formState.errors.numberOfDaysWorkedPerWeek
														?.message
												}
											</FormErrorMessage>
										</div>
									</FormControl>
									<FormControl
										isInvalid={!!form.formState.errors.phoneType}
										ref={(el) => (fieldRefs.current["phoneType"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												Phone Type
											</FormLabel>
											<Select
												{...form.register("phoneType")}
												rounded={0}
												h={{ base: 10, lg: 16 }}
											>
												<option value={""}>Select Option</option>
												{phoneTypeOptions.map((option) => (
													<option key={option.value} value={option.value}>
														{option.label}
													</option>
												))}
											</Select>
											<FormHelperText>
												This helps us know how best to send you job updates and
												support you
											</FormHelperText>
											<FormErrorMessage>
												{form.formState.errors.phoneType?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
									<FormControl
										isInvalid={!!form.formState.errors.leadSource}
										ref={(el) => (fieldRefs.current["leadSource"] = el)}
									>
										<div className="flex flex-col gap-3">
											<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
												How did you hear about LaborHack?
											</FormLabel>
											<Select
												// if the lead source is already set, don't allow the user to change it
												disabled={!!profile?.proApplication?.leadSource}
												{...form.register("leadSource")}
												rounded={0}
												h={{ base: 10, lg: 16 }}
											>
												<option value={""}>Select Option</option>
												{leadSourceOptions.map((option) => (
													<option key={option.value} value={option.value}>
														{option.label}
													</option>
												))}
											</Select>
											<FormErrorMessage>
												{form.formState.errors.leadSource?.message}
											</FormErrorMessage>
										</div>
									</FormControl>
									{leadSource === ProLeadSource.OTHER && (
										<FormControl
											isInvalid={!!form.formState.errors.leadSourceText}
											ref={(el) => (fieldRefs.current["leadSourceText"] = el)}
										>
											<div className="flex flex-col gap-3">
												<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
													Please specify how you heard about us
												</FormLabel>
												<Input
													disabled={
														!!profile?.proApplication?.leadSource ||
														!!profile?.proApplication?.leadSourceText
													}
													{...form.register("leadSourceText")}
													type="text"
													rounded={0}
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													border={"1px solid #D7D7D7"}
												/>
												<FormErrorMessage>
													{form.formState.errors.leadSourceText?.message}
												</FormErrorMessage>
											</div>
										</FormControl>
									)}
									{multiSelectReasonsEnabled ? (
										<>
											<FormControl
												isInvalid={
													!!form.formState.errors.reasonsForJoiningLaborHack
												}
												ref={(el) =>
													(fieldRefs.current["reasonForJoiningLaborHack"] = el)
												}
											>
												<div className="flex flex-col gap-3">
													<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
														Why did you join LaborHack?
													</FormLabel>
													<div className="flex flex-wrap gap-3">
														{reasonsForJoiningLaborHackOptions.map(
															({ label, value }) => {
																const isSelected =
																	reasonsForJoiningLaborHack?.includes(value);
																const isDisabled =
																	!!profile?.proApplication
																		?.reasonsForJoiningLaborHack?.length;
																return (
																	<button
																		key={value}
																		type="button"
																		disabled={isDisabled}
																		onClick={() => {
																			const current =
																				reasonsForJoiningLaborHack ?? [];
																			const updated = current.includes(value)
																				? current.filter(
																						(r: ReasonForJoiningLaborHack) =>
																							r !== value
																				  )
																				: [...current, value];
																			form.setValue(
																				"reasonsForJoiningLaborHack",
																				updated,
																				{ shouldValidate: true }
																			);
																		}}
																		className={`
																			px-4 py-3 rounded-full border text-sm lg:text-base font-medium
																			transition-colors duration-150
																			${
																				isDisabled
																					? "cursor-not-allowed opacity-60"
																					: "cursor-pointer"
																			}
																			${
																				isSelected
																					? "bg-[#121212] text-white border-[#121212]"
																					: "bg-white text-[#464646] border-[#D7D7D7] hover:border-[#121212]"
																			}
																		`}
																	>
																		{label}
																	</button>
																);
															}
														)}
													</div>
													<FormErrorMessage>
														Please select at least one reason
													</FormErrorMessage>
												</div>
											</FormControl>
											{reasonsForJoiningLaborHack?.includes(
												ReasonForJoiningLaborHack.OTHER
											) && (
												<FormControl
													isInvalid={
														!!form.formState.errors
															.reasonForJoiningLaborHackOtherText
													}
													ref={(el) =>
														(fieldRefs.current[
															"reasonForJoiningLaborHackOtherText"
														] = el)
													}
												>
													<div className="flex flex-col gap-3">
														<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
															Please specify why you joined LaborHack
														</FormLabel>
														<Input
															disabled={
																!!profile?.proApplication
																	?.reasonsForJoiningLaborHack?.length ||
																!!profile?.proApplication
																	?.reasonForJoiningLaborHackOtherText
															}
															{...form.register(
																"reasonForJoiningLaborHackOtherText"
															)}
															type="text"
															rounded={0}
															py={{ base: 5, lg: 8 }}
															px={{ base: 3, lg: 5 }}
															border={"1px solid #D7D7D7"}
														/>
														<FormErrorMessage>
															{
																form.formState.errors
																	.reasonForJoiningLaborHackOtherText?.message
															}
														</FormErrorMessage>
													</div>
												</FormControl>
											)}
										</>
									) : (
										<>
											<FormControl
												isInvalid={
													!!form.formState.errors.reasonForJoiningLaborHack
												}
												ref={(el) =>
													(fieldRefs.current["reasonForJoiningLaborHack"] = el)
												}
											>
												<div className="flex flex-col gap-3">
													<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
														Why did you join LaborHack?
													</FormLabel>
													<Select
														isDisabled={
															!!profile?.proApplication?.reasonForJoiningLaborHack
														}
														{...form.register("reasonForJoiningLaborHack")}
														rounded={0}
														h={{ base: 10, lg: 16 }}
													>
														<option value={""}>Select Option</option>
														{reasonsForJoiningLaborHackOptions.map(
															({ label, value }) => (
																<option key={label} value={value}>
																	{label}
																</option>
															)
														)}
													</Select>
													<FormErrorMessage>
														{
															form.formState.errors.reasonForJoiningLaborHack
																?.message
														}
													</FormErrorMessage>
												</div>
											</FormControl>
											{reasonForJoiningLaborHack ===
												ReasonForJoiningLaborHack.OTHER && (
												<FormControl
													isInvalid={
														!!form.formState.errors
															.reasonForJoiningLaborHackOtherText
													}
													ref={(el) =>
														(fieldRefs.current[
															"reasonForJoiningLaborHackOtherText"
														] = el)
													}
												>
													<div className="flex flex-col gap-3">
														<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
															Please specify why you joined LaborHack
														</FormLabel>
														<Input
															disabled={
																!!profile?.proApplication
																	?.reasonForJoiningLaborHack ||
																!!profile?.proApplication
																	?.reasonForJoiningLaborHackOtherText
															}
															{...form.register(
																"reasonForJoiningLaborHackOtherText"
															)}
															type="text"
															rounded={0}
															py={{ base: 5, lg: 8 }}
															px={{ base: 3, lg: 5 }}
															border={"1px solid #D7D7D7"}
														/>
														<FormErrorMessage>
															{
																form.formState.errors
																	.reasonForJoiningLaborHackOtherText?.message
															}
														</FormErrorMessage>
													</div>
												</FormControl>
											)}
										</>
									)}
								</div>

								<FormControl
									isInvalid={!hasTags && !!form.formState.errors.tags}
									ref={(el) => (fieldRefs.current["tags"] = el)}
								>
									<div className="flex flex-col gap-3">
										<FormField
											control={form.control}
											name="tags"
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-base lg:text-xl text-[#212121] font-medium">
														Profession tags
													</FormLabel>
													<div className="flex flex-wrap gap-2">
														{field.value?.map((tagId) => {
															const tag = tagsData?.jobAdTags.find(
																(t) => t.id === tagId
															);
															return (
																<Badge
																	key={tagId}
																	className="px-3 py-1 text-sm rounded-full"
																>
																	{tag?.name}
																	<button
																		type="button"
																		className="ml-2 rounded-full outline-none hover:bg-accent"
																		onClick={() => {
																			field.onChange(
																				field.value?.filter(
																					(id) => id !== tagId
																				)
																			);
																		}}
																	>
																		<XIcon className="w-3 h-3" />
																	</button>
																</Badge>
															);
														})}
														{customTags.map((tagId) => {
															const tag = profile?.customTags?.find(
																(t) => t.id === tagId
															);
															return (
																<Badge
																	key={`custom-${tagId}`}
																	className="px-3 py-1 text-sm rounded-full"
																>
																	{tag?.name || tagId}
																	<button
																		type="button"
																		className="ml-2 rounded-full outline-none hover:bg-accent"
																		onClick={() => {
																			const updatedTags = customTags.filter(
																				(t) => t !== tagId
																			);
																			form.setValue("customTags", updatedTags, {
																				shouldDirty: true,
																				shouldTouch: true,
																				shouldValidate: true,
																			});
																		}}
																	>
																		<XIcon className="w-3 h-3" />
																	</button>
																</Badge>
															);
														})}
														<Popover>
															<PopoverTrigger asChild>
																<PopoverTriggerButton
																	type="button"
																	variant="secondary"
																	size="sm"
																	className="h-8 rounded-full px-3"
																>
																	<PlusIcon className="w-4 h-4 mr-1" />
																	Add Profession Tag
																</PopoverTriggerButton>
															</PopoverTrigger>
															<PopoverContent className="p-0" align="start">
																<Command
																	filter={(value, search, keywords) => {
																		// Skip filtering if search is empty
																		if (!search) return 1;

																		const searchLower = search.toLowerCase();

																		// check if the value starts with the search term (highest priority)
																		if (
																			value
																				.toLowerCase()
																				.startsWith(searchLower)
																		)
																			return 1;

																		// check if any keyword starts with the search term
																		if (
																			keywords?.some((keyword) =>
																				keyword
																					.toLowerCase()
																					.startsWith(searchLower)
																			)
																		)
																			return 0.8;

																		// check if value contains the search term
																		if (
																			value.toLowerCase().includes(searchLower)
																		)
																			return 0.6;

																		// finally check if any keyword contains the search term
																		if (
																			keywords?.some((keyword) =>
																				keyword
																					.toLowerCase()
																					.includes(searchLower)
																			)
																		)
																			return 0.4;

																		// No match
																		return 0;
																	}}
																>
																	<CommandInput placeholder="Search tags..." />
																	<CommandList>
																		{Object.entries(tagGroups || {}).map(
																			([group, tags], index) => (
																				<Fragment key={group}>
																					<CommandGroup heading={group}>
																						{tags.map((tag) => (
																							<CommandItem
																								key={tag.id}
																								value={tag.name}
																								keywords={[tag.group]}
																								onSelect={() => {
																									if (
																										!field.value?.includes(
																											tag.id
																										)
																									) {
																										field.onChange([
																											...(field.value || []),
																											tag.id,
																										]);
																									}
																								}}
																								className="cursor-pointer hover:bg-accent"
																							>
																								{tag.name}
																							</CommandItem>
																						))}
																					</CommandGroup>
																					{index <
																						Object.keys(tagGroups || {})
																							.length -
																							1 && <CommandSeparator />}
																				</Fragment>
																			)
																		)}
																		<CommandEmpty>
																			<div className="py-3 px-2">
																				<p className="text-sm text-muted-foreground">
																					No tags found
																				</p>
																			</div>
																		</CommandEmpty>
																	</CommandList>
																</Command>
															</PopoverContent>
														</Popover>
													</div>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</FormControl>

								<div className="flex justify-end">
									<Button
										colorScheme="brand"
										type="submit"
										isLoading={updating}
									>
										Save Changes
									</Button>
								</div>
							</form>
						</Form>
					</div>
				</div>
			</div>
		</>
	);
}
